import React, { useState, useEffect } from 'react';
import { Play, Monitor, Download, X, FileDown, ExternalLink, ListChecks } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EspaceClientPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hasActiveOrder, setHasActiveOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  const downloads = [
    {
      id: 1,
      title: "Windows ISO",
      description: "Image Windows optimisée AqmOpti",
      url: "https://drive.google.com/file/d/18BqRGvagGff9pMOeL3xtcT97svGLAMYk/view?usp=drive_link",
      icon: FileDown,
      external: true
    },
    {
      id: 2,
      title: "Rufus 4.15",
      description: "Outil pour créer la clé USB bootable",
      url: "https://drive.usercontent.google.com/download?id=1QuuSVYFabzi2oSA09b0hnxbxorwWa2_O&export=download",
      icon: Download,
      external: true
    }
  ];

  const tutorials = [
    {
      id: 1,
      title: "Installation Windows avec clé USB",
      subtitle: "Nécessite une clé USB de 8,5 Go minimum",
      badge: "Avec clé USB",
      videoUrl: "https://customer-assets-eiarnc6j.emergentagent.net/job_web-bot-nexus/artifacts/ckvti07z_Tutoriel%20Windows%20Cl%C3%A9%20Usb%20Aqmopti.mp4",
      icon: Download
    },
    {
      id: 2,
      title: "Installation Windows sans clé USB",
      subtitle: "Aucune clé USB requise",
      badge: "Sans clé USB",
      videoUrl: "https://customer-assets-eiarnc6j.emergentagent.net/job_web-bot-nexus/artifacts/d8dp908q_Tutoriels%20sans%20Cl%C3%A9%20USB%20.mp4",
      icon: Monitor
    }
  ];

  useEffect(() => {
    // Wait for auth context to finish loading before deciding redirect.
    // Without this, on direct URL visit (or external link like aqmoptimisation.fr/espace-client)
    // `isAuthenticated` is `false` on first render → instant redirect to "/" before
    // the token-based user fetch completes.
    if (authLoading) return;

    const checkAccess = async () => {
      if (!isAuthenticated) {
        navigate('/');
        return;
      }

      // Les admins ont toujours accès à l'espace client
      if (user?.is_admin) {
        setHasActiveOrder(true);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        // Allow access if: user has Discord Client role OR has an active licence
        const isClient = user && user.is_client === true;
        
        if (isClient) {
          setHasActiveOrder(true);
          setLoading(false);
          return;
        }
        
        // Otherwise check licence-status
        const response = await fetch(`${API_URL}/api/user/licence-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.has_licence === true) {
            setHasActiveOrder(true);
          } else {
            setHasActiveOrder(false);
            navigate('/services');
          }
        } else {
          setHasActiveOrder(false);
          navigate('/services');
        }
      } catch (error) {
        console.error('Error checking access:', error);
        navigate('/services');
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [authLoading, isAuthenticated, navigate, user]);

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!hasActiveOrder) return null;

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-4">
            Espace Client
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Espace <span className="text-cyan-500">Client</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Téléchargez les fichiers nécessaires pour installer Windows de façon optimale
          </p>
        </div>

        {/* Warning Banner */}
        <div className="mb-8 p-4 bg-red-950/30 border border-red-500/40 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-red-400 text-base">⚠️</span>
            <h3 className="text-red-400 font-bold text-sm uppercase tracking-wide">Pensez à sauvegarder vos données</h3>
          </div>
          
          <p className="text-gray-300 text-sm mb-3">
            L'installation de Windows va <span className="text-red-400 font-semibold">formater votre disque dur</span>. Assurez-vous d'avoir sauvegardé :
          </p>
          
          <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm">
            <span className="flex items-center gap-1.5 text-gray-300">
              <span className="w-1 h-1 bg-red-400 rounded-full"></span>
              Fichiers personnels
            </span>
            <span className="flex items-center gap-1.5 text-gray-300">
              <span className="w-1 h-1 bg-red-400 rounded-full"></span>
              Fichiers dans la clé USB
            </span>
            <span className="flex items-center gap-1.5 text-gray-300">
              <span className="w-1 h-1 bg-red-400 rounded-full"></span>
              Il faudra vous reconnecter à vos comptes (Fortnite, Discord, etc.)
            </span>
          </div>
          
          <p className="text-red-400/80 text-xs">
            ❌ AqmOpti ne peut pas être tenu responsable de la perte de données.
          </p>
        </div>

        {/* Guide en 5 étapes */}
        <div className="mb-12" data-testid="install-steps">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-cyan-400" />
            Ton installation en 5 étapes
          </h2>
          <div className="space-y-3">
            {[
              { n: 1, title: "Sauvegarde tes données", desc: "L'installation formate ton disque : sauvegarde tes fichiers et note tes identifiants (Fortnite, Discord…)." },
              { n: 2, title: "Télécharge Windows + Rufus", desc: "Récupère l'ISO Windows et Rufus dans la section Téléchargements plus bas." },
              { n: 3, title: "Prépare ton installation", desc: "Deux méthodes au choix : avec clé USB (via Rufus, 8,5 Go min.) ou sans clé USB. Choisis le tuto qui te correspond." },
              { n: 4, title: "Installe Windows", desc: "Suis le tuto vidéo correspondant à ta méthode (avec ou sans clé USB) juste en dessous." },
              { n: 5, title: "Paramètre tes applications", desc: "Configure tes apps (Discord, Spotify, OBS, Steam…) grâce au salon Discord dédié." },
            ].map((s) => (
              <div
                key={s.n}
                data-testid={`install-step-${s.n}`}
                className="flex items-start gap-4 p-4 bg-zinc-900/50 border border-cyan-900/30 rounded-xl"
              >
                <div className="w-9 h-9 rounded-full bg-cyan-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">
                  {s.n}
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <h3 className="text-white font-semibold text-sm mb-0.5">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                  {s.n === 5 && (
                    <a
                      href="https://discord.com/channels/1171511163742343258/1534281405943648256"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="apps-channel-link"
                      className="inline-flex items-center gap-1.5 mt-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors"
                    >
                      Ouvrir le salon paramétrage <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tutoriels vidéo */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Play className="w-6 h-6 text-cyan-400" />
            Tutoriels d'installation Windows
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tutorials.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveVideo(t)}
                data-testid={`tutorial-card-${t.id}`}
                className="text-left flex items-center gap-4 p-5 bg-zinc-900/50 border border-cyan-900/30 rounded-xl hover:border-cyan-600/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-lg bg-cyan-950/50 border border-cyan-800/50 flex items-center justify-center group-hover:bg-cyan-600 transition-all duration-300 flex-shrink-0">
                  <Play className="w-6 h-6 text-cyan-500 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-[10px] font-semibold text-cyan-500 uppercase tracking-wider mb-1">
                    {t.badge}
                  </span>
                  <h3 className="text-white font-semibold mb-0.5 group-hover:text-cyan-400 transition-colors">
                    {t.title}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {t.subtitle}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Téléchargements */}
        <div>
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <FileDown className="w-6 h-6 text-cyan-400" />
            Téléchargements
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {downloads.map((download) => {
              const Icon = download.icon;
              return (
                <a
                  key={download.id}
                  href={download.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={!download.external}
                  className="flex items-center gap-4 p-5 bg-zinc-900/50 border border-cyan-900/30 rounded-xl hover:border-cyan-600/50 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-cyan-950/50 border border-cyan-800/50 flex items-center justify-center group-hover:bg-cyan-600 transition-all duration-300 flex-shrink-0">
                    <Icon className="w-6 h-6 text-cyan-500 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1 group-hover:text-cyan-400 transition-colors">
                      {download.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {download.description}
                    </p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-cyan-400 transition-colors flex-shrink-0" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Modal Vidéo */}
        {activeVideo && (
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setActiveVideo(null)}
          >
            <div 
              className="bg-zinc-900 border border-cyan-900/30 rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-cyan-900/30">
                <div>
                  <span className="inline-block text-xs font-semibold text-cyan-500 uppercase tracking-wider mb-2">
                    {activeVideo.badge}
                  </span>
                  <h3 className="text-2xl font-bold text-white">
                    {activeVideo.title}
                  </h3>
                </div>
                <button
                  onClick={() => setActiveVideo(null)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400 hover:text-white" />
                </button>
              </div>

              {/* Video */}
              <div className="aspect-video bg-black">
                <video
                  controls
                  autoPlay
                  className="w-full h-full"
                  src={activeVideo.videoUrl}
                >
                  Votre navigateur ne supporte pas la vidéo.
                </video>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-cyan-900/30">
                <p className="text-gray-400 text-sm">
                  {activeVideo.subtitle}
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EspaceClientPage;
